import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ANALYTICS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend, BarChart, Bar,
} from "recharts";
import { AlertTriangle, TrendingUp, Users } from "lucide-react";

const riskColor = {
  low: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-rose-100 text-rose-700",
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setData(await api.analytics()); }
      catch { toast.error(t.common.failed); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid={ANALYTICS.root}>
      <PageHeader title={t.analytics.title} description={t.analytics.description} />
      {loading || !data ? (
        <div className="text-slate-400 text-sm">{t.common.loading}</div>
      ) : (
        <>
          <div className="card-soft p-6 mb-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <div className="font-display font-semibold text-slate-900">{t.analytics.growth30d}</div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.series_30d}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => v.slice(5)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" name="просмотры" dataKey="views" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" name="лайки" dataKey="likes" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  <Line type="monotone" name="подписки" dataKey="follows" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  <Line type="monotone" name="комменты" dataKey="comments" stroke="#a855f7" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="card-soft p-6 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-4 w-4 text-emerald-500" />
                <div className="font-display font-semibold text-slate-900">{t.analytics.perAccountHealth}</div>
              </div>
              {data.per_account.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center">{t.analytics.noAccounts}</div>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.per_account}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="username" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, fontSize: 12 }} />
                      <Bar dataKey="health" fill="#10b981" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="card-soft p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <div className="font-display font-semibold text-slate-900">{t.analytics.shadowRisk}</div>
              </div>
              <div className="space-y-2">
                {data.per_account.map((a) => (
                  <div key={a.account_id} className="flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{a.username}</div>
                      <div className="text-xs text-slate-500">{a.followers.toLocaleString("ru-RU")} подп.</div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full ${riskColor[a.shadowban_risk]}`}>
                      {t.analytics.risk[a.shadowban_risk] || a.shadowban_risk}
                    </span>
                  </div>
                ))}
                {data.per_account.length === 0 && (
                  <div className="text-sm text-slate-400 text-center py-4">{t.analytics.noAccounts}</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
